import { Fragment, memo, useMemo } from 'react';
import { roundBook, fmtBook } from '../../utils/casinoBook';

const CDN = 'https://wver.sprintstaticdata.com/v230/static/front/img/cards/';
const CARD_FACES = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
const LINE_GROUPS = [
  { faces: ['A', '2', '3'], sid: 20 },
  { faces: ['4', '5', '6'], sid: 21 },
  { faces: ['8', '9', '10'], sid: 22 },
  { faces: ['J', 'Q', 'K'], sid: 23 },
];

function isSusp(item) {
  return !item || item.gstatus === 'SUSPENDED' || item.b === 0 || item.b === '0';
}

function bookOf(exposures, name, sid) {
  return roundBook(exposures?.[`sid:${sid}`] ?? exposures?.[name] ?? 0);
}

function bookCls(v) {
  return v < 0 ? 'book-red' : v > 0 ? 'book-green' : 'book-black';
}

function fmtAmt(n) {
  const x = Number(n) || 0;
  if (x >= 100000) return `${x / 100000}L`;
  if (x >= 1000) return `${x / 1000}K`;
  return String(x);
}

// Range from the feed item (reference: `mainData[i].min/max`, `cardsdata[0]`),
// the old fixed values only as fallback — lucky7 100-1L/25K/5K, lucky7eu2 1L→3L,
// plucky7 1K-2L/50K/10K.
function MinMax({ item, min = 100, max, align = 'right' }) {
  return (
    <div className={`casino-min-max text-${align}`}>
      R:
      <span>{fmtAmt(item?.min ?? min)}</span>
      -
      <span>{fmtAmt(item?.max ?? max)}</span>
    </div>
  );
}

// lockTop (opt-in, Lucky 7 C): a locked Low/High or side-bet cell with a book
// pins its padlock to the top. The book stays where style.css puts it (absolute,
// under the box) — don't override its position or it jumps up into the box.
const BetTableLucky7 = memo(function BetTableLucky7({ gameData, onBet, exposures, lockTop = false }) {
  const sub = gameData?.sub ?? [];
  const handleBet = onBet || (() => {});

  const byId = useMemo(() => {
    const m = {};
    sub.forEach((s) => { m[s.sid] = s; });
    return m;
  }, [sub]);

  const lowItem = byId[1];
  const highItem = byId[2];
  const evenItem = byId[3];
  const oddItem = byId[4];
  const redItem = byId[5];
  const blackItem = byId[6];

  const click = (item) => { if (!isSusp(item)) handleBet(item.sid, 'back', item.b, item); };
  const book = (item) => bookOf(exposures, item?.nat, item?.sid);
  const lt = (item) => (lockTop && book(item) ? ' lock-top' : '');

  const cardOdds = byId[7]?.b || 0;

  // side bet (Even / Odd / Black / Red) — reference: item + MinMax siblings
  const sideBet = (item, buttons) => (
    <div className="lucky7-extra-bets-item-container">
      <div className={`lucky7-extra-bets-item${lt(item)}${isSusp(item) ? ' suspended' : ''}`} onClick={() => click(item)}>
        <div className="casino-odds">{item?.b || 0}</div>
        {' '}
        <div className="casino-buttons">{buttons}</div>
        {' '}
        <div className={`casino-book ${bookCls(book(item))}`}>{fmtBook(book(item))}</div>
      </div>
      {' '}
      <MinMax item={item} max={25000} align="right" />
    </div>
  );

  // single card item (reference: card-image + book, space between)
  const cardItem = (face, i) => {
    const item = byId[7 + i];
    return (
      <div key={face} className="casino-card-item" onClick={() => click(item)}>
        <div className={`card-image${isSusp(item) ? ' suspended' : ''}`}>
          <img src={`${CDN}${face}.png`} />
        </div>
        {' '}
        <div className={`casino-book ${bookCls(book(item))}`}>{fmtBook(book(item))}</div>
      </div>
    );
  };

  return (
    <>
      {/* Low / 7 card / High */}
      <div className="casino-box low-high-box">
        <div className={`low-odds${lt(lowItem)}${isSusp(lowItem) ? ' suspended' : ''}`} onClick={() => click(lowItem)}>
          <div className="casino-odds">{lowItem?.b || 0}</div>
          {' '}
          <div className="text-center casino-buttons"><span>Low Card</span></div>
          {' '}
          <div className={`casino-book ${bookCls(book(lowItem))}`}>{fmtBook(book(lowItem))}</div>
        </div>
        {' '}
        <div className="text-center lucky7-card">
          <img src={`${CDN}7.png`} className="img-fluid" />
        </div>
        {' '}
        <div className={`high-odds${lt(highItem)}${isSusp(highItem) ? ' suspended' : ''}`} onClick={() => click(highItem)}>
          <div className="casino-odds">{highItem?.b || 0}</div>
          {' '}
          <div className="text-center casino-buttons"><span>High Card</span></div>
          {' '}
          <div className={`casino-book ${bookCls(book(highItem))}`}>{fmtBook(book(highItem))}</div>
        </div>
        {' '}
        <MinMax item={lowItem} max={100000} align="right" />
      </div>
      {' '}
      {/* Even / Odd / Black / Red */}
      <div className="casino-box sidebets-box">
        <div className="lucky7-extra-bets">
          {sideBet(evenItem, 'Even')}
          {' '}
          {sideBet(oddItem, 'Odd')}
          {' '}
          {sideBet(blackItem, <><img src={`${CDN}spade.png`} />{' '}<img src={`${CDN}club.png`} /></>)}
          {' '}
          {sideBet(redItem, <><img src={`${CDN}heart.png`} />{' '}<img src={`${CDN}diamond.png`} /></>)}
        </div>
      </div>
      {' '}
      {/* Line groups (A-2-3, 4-5-6, 8-9-10, J-Q-K) — book cards-top-box ke
          BAAD, MinMax casino-cards ke baad (reference) */}
      <div className="casino-box cards-top">
        <div className="container-fluid container-fluid-5">
          <div className="row row5">
            {LINE_GROUPS.map((line, li) => {
              const item = byId[line.sid];
              return (
                <Fragment key={line.sid}>
                  {li > 0 && ' '}
                  <div className="col-6 col-md-3">
                    <div onClick={() => click(item)}>
                      <div className="casino-odds">{item?.b || 0}</div>
                      {' '}
                      <div className="casino-cards text-center mt-1">
                        <div className={`casino-box cards-top-box${isSusp(item) ? ' suspended' : ''}`}>
                          {line.faces.map((face, fi) => (
                            <Fragment key={face}>
                              {fi > 0 && ' '}
                              <div className="casino-card-item">
                                <div className="card-image"><img src={`${CDN}${face}.png`} /></div>
                              </div>
                            </Fragment>
                          ))}
                        </div>
                        {' '}
                        <div className={`w-100 casino-book ${bookCls(book(item))}`}>{fmtBook(book(item))}</div>
                      </div>
                      {' '}
                      <MinMax item={item} max={5000} align="center" />
                    </div>
                  </div>
                </Fragment>
              );
            })}
          </div>
        </div>
      </div>
      {' '}
      {/* Individual 13 cards — A-7 / 8-K (reference: space only between 7 and 8);
          MinMax .w-100 ke BAHAR */}
      <div className="casino-box cards-box">
        <div className="w-100">
          <div className="casino-odds">{cardOdds}</div>
          {' '}
          <div className="casino-cards text-center mt-1">
            {CARD_FACES.slice(0, 7).map((face, i) => cardItem(face, i))}
            {' '}
            {CARD_FACES.slice(7).map((face, i) => cardItem(face, i + 7))}
          </div>
        </div>
        {' '}
        <MinMax item={byId[7]} max={5000} align="right" />
      </div>
    </>
  );
});

export default BetTableLucky7;
